import React, { useEffect } from "react";
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./App.css";

// Components
import Home from "./components/Home";
import Page from "./components/Pagges.jsx";
import Products from "./components/Products/Products";
import ProductDetail from "./components/ProductDetails/ProductDetail";
import Wishlist from "./components/Wishlist/Wishlist.jsx";
import EmptyCart from "./components/EmptyCart/EmptyCart.jsx";
import Thankyoucart from "./components/Thankyou/Thankyoucart.jsx";
import AddAddress from "./components/EmptyCart/AddAddress.jsx";
import ProfilePage from "./components/Orders/ProfilePage";
import ProfileOrders from "./components/Orders/ProfileOrders";
import ProfileSubscription from "./components/Orders/ProfileSubscription";
import ProfileAddress from "./components/Orders/ProfileAddress";
import ProfileSettings from "./components/Orders/ProfileSettings";
import ProfileShare from "./components/Orders/ProfileShare";
import ProfileForm from "./components/Orders/ProfileForm.jsx";
import OrderDetailsReturned from "./components/OrderDetails/OrderDetailsReturned.jsx";
import ProfileOrderDetails from "./components/Orders/ProfileOrderDetails.jsx";
import PurchaseOrderDetails from "./components/Orders/PurchaseOrderDetails.jsx";
import OrderPayPage from "./components/Orders/OrderPayPage .jsx";
import PrivacyPolicy from "./components/Legal/privacypolicy.jsx";
import Terms from "./components/Legal/Terms.jsx";
import Aboutus from "./components/Legal/Aboutus.jsx";
import Cartsection from "./components/EmptyCart/Cartsection.jsx";
import Cartpage from "./components/EmptyCart/Cartpage.jsx";
import { ProductProvider } from "./context/ProductContext";
import LoginSuccess from "./components/success.jsx";
import PaymentSuccess from "./components/PaymentSuccess.jsx";
import SubPaymentSuccess from "./components/SubPaymentSuccess.jsx";
import LoginFail from './components/SubFail.jsx';
import ProfileSubscriptionHistory from './components/Orders/ProfileSubscriptionHistory.jsx';
import { AuthProvider, useAuth } from "./context/AuthContext";

// ✅ Global Fetch Interceptor for token expiry
const GlobalFetchInterceptor = ({ children }) => {
  const { token, logout } = useAuth();

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const [url, config] = args;

      const newConfig = {
        ...(config || {}),
        headers: {
          ...(config?.headers || {}),
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      const response = await originalFetch(url, newConfig);

      if (response.status === 401) {
        const data = await response.clone().json().catch(() => null);
        if (data?.message === "Token has expired") {
          logout();
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [token, logout]);

  return children;
};

// ✅ ProtectedRoute
const ProtectedRoute = () => {
  const token = localStorage.getItem("auth_token");
  return token ? <Outlet /> : <Navigate to="/login" replace />;
};

// ✅ App Component
function App() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    // Show alert if backend sent a message in query params
    const params = new URLSearchParams(location.search);
    const message = params.get("message");

    if (message) {
      window.alert(message); // show alert

      // Remove the query params so alert doesn't show again
      const newUrl = window.location.origin + location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      // Redirect to login page
      navigate("/login", { replace: true });
    }
  }, [location, navigate]);

  return (
    <AuthProvider>
      <GlobalFetchInterceptor>
        <Routes>
          <Route path="/success" element={<LoginSuccess />} />
          <Route path="/paymentsuccess/:orderId" element={<PaymentSuccess />} />
          <Route path="/Subpaymentsuccess/:orderId" element={<SubPaymentSuccess />} />
          <Route path="/SubFail/:orderId" element={<LoginFail />} />

          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route
            path="/product/:id"
            element={
              <ProductProvider>
                <ProductDetail />
              </ProductProvider>
            }
          />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/EmptyCart" element={<EmptyCart />} />
            <Route path="/Cartsection" element={<Cartsection />} />
            <Route path="/Cartpage" element={<Cartpage />} />
            <Route path="/Thankyoucart" element={<Thankyoucart />} />
            <Route path="/Address" element={<AddAddress />} />
            <Route path="/OrderDetails" element={<OrderDetailsReturned />} />

            <Route path="/profile" element={<ProfilePage />}>
              <Route index element={<ProfileForm />} />
              <Route path="orders" element={<ProfileOrders />} />
              <Route path="orders/:orderId" element={<ProfileOrderDetails />} />
              <Route path="orders/:orderId/repeat" element={<OrderPayPage />} />
              <Route path="purchases/:purchaseId" element={<PurchaseOrderDetails />} />
              <Route path="subscription" element={<ProfileSubscription />} />
              <Route path="subscriptionhistory" element={<ProfileSubscriptionHistory />} />
              <Route path="address" element={<ProfileAddress />} />
              <Route path="settings" element={<ProfileSettings />} />
              <Route path="share" element={<ProfileShare />} />
            </Route>
          </Route>

          {/* Legal */}
          <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
          <Route path="/Terms" element={<Terms />} />
          <Route path="/Aboutus" element={<Aboutus />} />

          {/* Catch-all route at the end */}
          <Route path="/*" element={<Page />} />
        </Routes>
      </GlobalFetchInterceptor>
    </AuthProvider>
  );
}

export default App;
